// PlanosView — admin global gerencia planos da plataforma (Free, Pro, etc.).
//
// Por plano: nome, preço mensal (cents), descrição, features (JSON livre)
// e flags (isActive, displayOrder).
//
// Features ficam num jsonb editável como pares chave/valor. Conhecidas
// (com chips):
//   - ai (boolean): libera Estudar com IA + Assistente IA
//   - exportPdf (boolean): libera exportação do PDF do banco
//   - maxBanks (number | null): teto de bancos (null = ilimitado)
//   - maxProvas (number | null): teto de provas (null = ilimitado)
// O admin pode adicionar features customizadas — só não terão UI especial.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowRight,
  Check,
  CheckCircle2,
  CreditCard,
  Loader2,
  Plus,
  Save,
  Search,
  Sparkles,
  Star,
  Trash2,
  Users,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { Switch } from "@app/ui/components/switch";
import {
  api,
  ApiError,
  type PlanMemberRow,
  type PlatformPlanRow,
  type UserRow
} from "./api";
import { openAlert, openConfirm } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const phase = signal<"loading" | "ready" | "error">("loading");
const plans = signal<PlatformPlanRow[]>([]);
const errMsg = signal<string | null>(null);
const membersOf = signal<PlatformPlanRow | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listPlatformPlans();
    plans.value = r.plans;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

function brl(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

export function PlanosView() {
  useEffect(() => {
    void load();
  }, []);

  if (phase.value === "loading") {
    return (
      <div class="space-y-2">
        <Skeleton class="h-32 w-full" />
        <Skeleton class="h-32 w-full" />
      </div>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-4 py-3 text-sm">
        <Sparkles className="mr-1 inline size-4 text-violet-600" />
        <strong>Planos da plataforma.</strong> Cada usuário tem um plano
        (default <code class="rounded bg-muted px-1">free</code>). Mudar
        recursos aqui afeta imediatamente quem está logado — frontend lê via{" "}
        <code class="rounded bg-muted px-1">/api/student/platform-plan</code>.
      </div>

      <div class="space-y-3">
        {plans.value.map((p) => (
          <PlanCard key={p.id} plan={p} />
        ))}
      </div>

      <CreatePlanButton />

      {membersOf.value && (
        <MembersModal
          plan={membersOf.value}
          onClose={() => (membersOf.value = null)}
        />
      )}
    </div>
  );
}

// =====================
// Modal "Membros do plano"
// =====================
//
// Lista quem está atribuído ao plano (com status de cobrança) + permite
// adicionar mais usuários via busca por nome/email. Útil pra admin curar
// planos restritos (Pro Admin) e ver inadimplentes de planos pagos.

function MembersModal({
  plan,
  onClose
}: {
  plan: PlatformPlanRow;
  onClose: () => void;
}) {
  const [members, setMembers] = useState<PlanMemberRow[] | null>(null);
  const [summary, setSummary] = useState<{
    total: number;
    active: number;
    pastDue: number;
    paused: number;
  } | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [allUsers, setAllUsers] = useState<UserRow[] | null>(null);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.listPlanMembers(plan.slug);
      setMembers(r.members);
      setSummary(r.summary);
      setNote(r.note ?? null);
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // Carrega lista de todos os usuários pra adicionar — feito 1x por modal
    void api.listUsers().then((r) => setAllUsers(r.users));
  }, [plan.slug]);

  async function addUser(u: UserRow) {
    setBusyUserId(u.id);
    try {
      await api.assignPlan({ userId: u.id, slug: plan.slug });
      await load();
    } catch (e) {
      void openAlert({
        title: "Erro ao adicionar",
        message: friendlyError(e)
      });
    } finally {
      setBusyUserId(null);
    }
  }

  // Filtragem da lista de usuários pra adicionar: exclui quem já é membro,
  // e aplica busca por nome/email.
  const memberIds = new Set((members ?? []).map((m) => m.userId));
  const candidates =
    !search.trim() || !allUsers
      ? []
      : allUsers
          .filter((u) => !memberIds.has(u.id))
          .filter((u) => {
            const q = search.toLowerCase();
            return (
              u.name.toLowerCase().includes(q) ||
              u.email.toLowerCase().includes(q)
            );
          })
          .slice(0, 10);

  // Modal admin não fecha por click no backdrop. [[feedback-modal-no-click-outside-close]]

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4 backdrop-blur-sm"
    >
      <div class="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div class="min-w-0 flex-1">
            <h2 class="flex items-center gap-2 text-base font-bold">
              <Users className="size-4 text-primary" />
              Membros do plano {plan.name}
            </h2>
            <p class="truncate text-[11px] text-muted-foreground">
              {plan.description ?? plan.slug}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="flex-1 space-y-4 overflow-y-auto p-5">
          {/* KPIs */}
          {summary && (
            <div class="grid grid-cols-3 gap-2 text-center text-xs">
              <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-2">
                <div class="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Ativos
                </div>
                <div class="text-xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                  {summary.active}
                </div>
              </div>
              <div class="rounded-md border border-destructive/40 bg-destructive/5 p-2">
                <div class="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Inadimplentes
                </div>
                <div class="text-xl font-bold tabular-nums text-destructive">
                  {summary.pastDue}
                </div>
              </div>
              <div class="rounded-md border border-amber-500/30 bg-amber-500/5 p-2">
                <div class="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Pausados
                </div>
                <div class="text-xl font-bold tabular-nums text-amber-700 dark:text-amber-300">
                  {summary.paused}
                </div>
              </div>
            </div>
          )}

          {note && (
            <div class="rounded-md border border-blue-500/30 bg-blue-500/5 px-3 py-2 text-xs text-blue-700 dark:text-blue-300">
              <AlertCircle className="mr-1 inline size-3.5" />
              {note}
            </div>
          )}

          {/* Busca pra adicionar */}
          {plan.slug !== "free" && (
            <section>
              <h3 class="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Adicionar usuário ao plano
              </h3>
              <div class="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
                  placeholder="Buscar por nome ou email…"
                  class="w-full rounded-md border border-border bg-background py-2 pl-7 pr-3 text-sm"
                />
              </div>
              {candidates.length > 0 && (
                <ul class="mt-2 space-y-1">
                  {candidates.map((u) => (
                    <li
                      key={u.id}
                      class="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm"
                    >
                      <div class="min-w-0 flex-1">
                        <div class="truncate font-medium">{u.name}</div>
                        <div class="truncate text-[11px] text-muted-foreground">
                          {u.email}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => addUser(u)}
                        disabled={busyUserId === u.id}
                        class="inline-flex shrink-0 items-center gap-1 rounded-md bg-primary px-2 py-1 text-[11px] font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
                      >
                        {busyUserId === u.id ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          <Plus className="size-3" />
                        )}
                        Adicionar
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {search.trim() && candidates.length === 0 && allUsers && (
                <p class="mt-2 text-[11px] text-muted-foreground">
                  Nenhum usuário encontrado, ou todos já são membros.
                </p>
              )}
            </section>
          )}

          {/* Lista de membros */}
          <section>
            <h3 class="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Membros atuais ({members?.length ?? 0})
            </h3>
            {loading ? (
              <p class="text-sm text-muted-foreground">Carregando…</p>
            ) : !members || members.length === 0 ? (
              <div class="rounded-md border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground">
                Nenhum usuário neste plano ainda.
              </div>
            ) : (
              <ul class="divide-y divide-border overflow-hidden rounded-md border border-border bg-card/40">
                {members.map((m) => (
                  <MemberRow key={m.subscriptionId} member={m} />
                ))}
              </ul>
            )}
          </section>
        </div>

        <footer class="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-accent"
          >
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}

function MemberRow({ member }: { member: PlanMemberRow }) {
  const map = {
    active: { label: "ativo", class: "text-emerald-600" },
    paused: { label: "pausado", class: "text-amber-600" },
    past_due: { label: "INADIMPLENTE", class: "text-destructive font-semibold" },
    canceled: { label: "cancelado", class: "text-muted-foreground" }
  }[member.status];
  return (
    <li class="flex items-center gap-3 px-3 py-2 text-xs">
      {member.status === "past_due" ? (
        <AlertCircle className="size-4 shrink-0 text-destructive" />
      ) : (
        <CheckCircle2 className="size-4 shrink-0 text-emerald-600" />
      )}
      <div class="min-w-0 flex-1">
        <div class="flex items-baseline gap-2">
          <span class="truncate font-medium">{member.userName}</span>
          <span class="truncate text-muted-foreground">{member.userEmail}</span>
        </div>
        <div class="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
          <span class={map.class}>{map.label}</span>
          <span>·</span>
          <span>
            {member.billingType === "credit_card" ? "Cartão" : "Pix"}
          </span>
          <span>·</span>
          <span class="tabular-nums">
            Próxima:{" "}
            {new Date(member.nextChargeAt).toLocaleDateString("pt-BR")}
          </span>
          {member.failedAttempts > 0 && (
            <>
              <span>·</span>
              <span class="text-destructive">
                {member.failedAttempts} falha
                {member.failedAttempts !== 1 ? "s" : ""}
              </span>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

function PlanCard({ plan }: { plan: PlatformPlanRow }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(plan.name);
  const [description, setDescription] = useState(plan.description ?? "");
  const [priceReais, setPriceReais] = useState(
    (plan.monthlyCents / 100).toFixed(2).replace(".", ",")
  );
  const [features, setFeatures] = useState<Record<string, unknown>>(plan.features);
  const [isActive, setIsActive] = useState(plan.isActive);
  const [isPublic, setIsPublic] = useState(plan.isPublic);
  const [saving, setSaving] = useState(false);

  function parsePrice(): number {
    const cleaned = priceReais.replace(/[^\d,.-]/g, "").replace(",", ".");
    const n = parseFloat(cleaned);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.round(n * 100);
  }

  async function save() {
    setSaving(true);
    try {
      await api.updatePlatformPlan({
        id: plan.id,
        name: name.trim(),
        description: description.trim(),
        monthlyCents: parsePrice(),
        features,
        isActive,
        isPublic
      });
      await load();
      setEditing(false);
    } catch (e) {
      const msg = friendlyError(e);
      errMsg.value = msg;
    } finally {
      setSaving(false);
    }
  }

  const [migrationOpen, setMigrationOpen] = useState<
    null | { mode: "archive" | "hard-delete" }
  >(null);
  const [defaultBusy, setDefaultBusy] = useState(false);

  async function makeDefault() {
    setDefaultBusy(true);
    try {
      await api.updatePlatformPlan({ id: plan.id, isDefault: true });
      await load();
    } catch (e) {
      void openAlert({
        title: "Erro ao tornar default",
        message: friendlyError(e)
      });
    } finally {
      setDefaultBusy(false);
    }
  }

  const isFree = plan.slug === "free";

  if (!editing) {
    return (
      <div
        class={`relative overflow-hidden rounded-md p-5 transition ${
          plan.isDefault
            ? "border-2 border-emerald-500/70 bg-emerald-500/[0.04] shadow-[0_0_0_4px_rgba(16,185,129,0.08)]"
            : "border border-border bg-card"
        }`}
      >
        {plan.isDefault && (
          <div class="absolute inset-x-0 top-0 flex items-center justify-center bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-500 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
            <Star className="mr-1 size-3 fill-white" />
            Plano default para novos usuários
          </div>
        )}
        <div class={`flex items-start gap-3 ${plan.isDefault ? "mt-5" : ""}`}>
          <div
            class={`grid size-12 place-items-center rounded-md ${
              plan.isDefault
                ? "bg-emerald-500/15 text-emerald-600"
                : "bg-violet-500/15 text-violet-600"
            }`}
          >
            {plan.isDefault ? (
              <Star className="size-6 fill-emerald-600/20" />
            ) : (
              <CreditCard className="size-6" />
            )}
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-baseline gap-2">
              <h3 class="text-lg font-bold">{plan.name}</h3>
              <code class="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                {plan.slug}
              </code>
              {!plan.isActive && (
                <span class="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                  inativo
                </span>
              )}
              {!plan.isPublic && plan.isActive && !plan.archivedAt && (
                <span class="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
                  restrito
                </span>
              )}
              {plan.archivedAt && (
                <span class="rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-semibold text-destructive">
                  arquivado
                </span>
              )}
            </div>
            {plan.description && (
              <p class="mt-1 text-sm text-muted-foreground">
                {plan.description}
              </p>
            )}
            <div class="mt-2 flex flex-wrap items-baseline gap-3">
              <p class="text-2xl font-bold tabular-nums">
                {brl(plan.monthlyCents)}
                <span class="ml-1 text-xs font-normal text-muted-foreground">
                  /mês
                </span>
              </p>
              <button
                type="button"
                onClick={() => (membersOf.value = plan)}
                class="inline-flex items-center gap-1.5 rounded-full bg-violet-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-violet-700 hover:bg-violet-500/15 dark:text-violet-300"
                title="Ver membros do plano"
              >
                <Users className="size-3" />
                {plan.memberCount ?? 0} {plan.memberCount === 1 ? "membro" : "membros"}
              </button>
            </div>
            <div class="mt-3 flex flex-wrap gap-1.5">
              {renderFeatureChips(plan.features)}
            </div>
          </div>
          <div class="flex shrink-0 flex-col gap-1">
            <button
              type="button"
              onClick={() => (membersOf.value = plan)}
              class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-primary hover:text-primary"
            >
              <Users className="size-3" />
              Membros
            </button>
            <button
              type="button"
              onClick={() => setEditing(true)}
              class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-primary hover:text-primary"
            >
              Editar
            </button>
            {!plan.isDefault && plan.isActive && !plan.archivedAt && (
              <button
                type="button"
                onClick={makeDefault}
                disabled={defaultBusy}
                class="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-500/20 disabled:opacity-50 dark:text-emerald-300"
                title="Tornar este o plano default pra novos usuários"
              >
                {defaultBusy ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <Star className="size-3" />
                )}
                Tornar default
              </button>
            )}
            {!isFree && !plan.archivedAt && (
              <button
                type="button"
                onClick={() => setMigrationOpen({ mode: "archive" })}
                class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-amber-500 hover:text-amber-700"
              >
                <Trash2 className="size-3" />
                Arquivar
              </button>
            )}
            {!isFree && (
              <button
                type="button"
                onClick={() => setMigrationOpen({ mode: "hard-delete" })}
                class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs text-destructive hover:border-destructive hover:bg-destructive/10"
                title="Excluir permanentemente (precisa migrar membros)"
              >
                <Trash2 className="size-3" />
                Excluir
              </button>
            )}
          </div>
        </div>
        {migrationOpen && (
          <MigrateAndDeleteModal
            plan={plan}
            mode={migrationOpen.mode}
            onClose={() => setMigrationOpen(null)}
            onDone={async () => {
              setMigrationOpen(null);
              await load();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div class="space-y-3 rounded-md border border-primary/40 bg-primary/5 p-5">
      <div class="text-sm font-medium">
        Editando: <span class="font-bold">{plan.name}</span>{" "}
        <code class="rounded bg-muted px-1 text-xs">{plan.slug}</code>
      </div>
      <div class="grid gap-3 md:grid-cols-2">
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Nome
          </label>
          <input
            type="text"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            placeholder="Nome do plano"
            title="Nome do plano"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            disabled={saving}
          />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Preço mensal (R$)
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={priceReais}
            onInput={(e) => setPriceReais((e.target as HTMLInputElement).value)}
            placeholder="19,90"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm tabular-nums"
            disabled={saving || isFree}
          />
          {isFree && (
            <p class="mt-1 text-[10px] text-muted-foreground">
              Free é sempre R$ 0.
            </p>
          )}
        </div>
      </div>
      <div>
        <label class="mb-1 block text-xs font-medium text-muted-foreground">
          Descrição
        </label>
        <textarea
          value={description}
          onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
          rows={2}
          placeholder="Descrição do plano (mostrada ao aluno)"
          title="Descrição do plano"
          class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          disabled={saving}
        />
      </div>
      <FeaturesEditor features={features} onChange={setFeatures} disabled={saving} />
      <label class="flex items-center gap-3">
        <Switch
          checked={isActive}
          onCheckedChange={(v) => setIsActive(v)}
          disabled={saving}
        />
        <div>
          <div class="text-sm font-medium">Plano ativo</div>
          <p class="text-[10px] text-muted-foreground">
            Quando desligado, não aparece como opção de upgrade pro aluno.
          </p>
        </div>
      </label>
      <label class="flex items-center gap-3">
        <Switch
          checked={isPublic}
          onCheckedChange={(v) => setIsPublic(v)}
          disabled={saving}
        />
        <div>
          <div class="text-sm font-medium">Visível no catálogo público</div>
          <p class="text-[10px] text-muted-foreground">
            Quando desligado, o plano fica restrito — admin precisa atribuir
            manualmente a cada usuário (útil pra "Pro Admin", parceiros etc).
          </p>
        </div>
      </label>
      <div class="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setEditing(false)}
          disabled={saving}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !name.trim()}
          class="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          Salvar
        </button>
      </div>
    </div>
  );
}

// =====================
// Modal "Migrar membros e arquivar/excluir"
// =====================
//
// Abre quando admin clica em Arquivar OU Excluir. Mostra contador de
// membros ativos + seletor pra qual plano migrar (radio). Confirma a ação
// numa transação no server. Hard-delete (purga) só rola quando o plano
// está sem membros ativos no destino.

function MigrateAndDeleteModal({
  plan,
  mode,
  onClose,
  onDone
}: {
  plan: PlatformPlanRow;
  mode: "archive" | "hard-delete";
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const memberCount = plan.memberCount ?? 0;
  const candidates = plans.value.filter(
    (p) =>
      p.id !== plan.id && p.isActive && !p.archivedAt && p.slug !== plan.slug
  );
  const defaultCand = candidates.find((p) => p.isDefault);
  const [targetSlug, setTargetSlug] = useState<string>(
    defaultCand?.slug ?? candidates[0]?.slug ?? ""
  );
  const [busy, setBusy] = useState(false);

  async function run() {
    if (memberCount > 0 && !targetSlug) {
      void openAlert({
        title: "Escolha um plano destino",
        message: "Selecione pra qual plano migrar os membros atuais."
      });
      return;
    }
    const isHard = mode === "hard-delete";
    const ok = await openConfirm({
      title: isHard ? "Excluir permanentemente?" : "Arquivar plano?",
      message: isHard
        ? `Vou migrar ${memberCount} membro${memberCount === 1 ? "" : "s"} pra "${targetSlug}" e EXCLUIR "${plan.name}" para sempre. Essa ação não pode ser desfeita.`
        : `Vou migrar ${memberCount} membro${memberCount === 1 ? "" : "s"} pra "${targetSlug}" e arquivar "${plan.name}" (esconde do catálogo, mas mantém histórico).`,
      confirmLabel: isHard ? "Excluir permanentemente" : "Arquivar",
      danger: true
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.archiveOrDeletePlan({
        id: plan.id,
        targetSlug: memberCount > 0 ? targetSlug : null,
        hard: isHard
      });
      await onDone();
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    } finally {
      setBusy(false);
    }
  }

  // Modal admin não fecha por click no backdrop. [[feedback-modal-no-click-outside-close]]

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4 backdrop-blur-sm"
    >
      <div class="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div class="min-w-0">
            <h2 class="flex items-center gap-2 text-base font-bold">
              {mode === "hard-delete" ? (
                <Trash2 className="size-4 text-destructive" />
              ) : (
                <Trash2 className="size-4 text-amber-600" />
              )}
              {mode === "hard-delete"
                ? "Excluir plano permanentemente"
                : "Arquivar plano"}
            </h2>
            <p class="truncate text-[11px] text-muted-foreground">
              {plan.name} · {plan.slug}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="flex-1 space-y-4 overflow-y-auto p-5">
          {memberCount === 0 ? (
            <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-3 text-sm text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="mr-1 inline size-4" />
              Nenhum membro ativo neste plano — pode{" "}
              {mode === "hard-delete" ? "excluir" : "arquivar"} direto.
            </div>
          ) : (
            <>
              <div class="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2.5 text-sm">
                <AlertCircle className="mr-1 inline size-4 text-amber-600" />
                Existem <strong>{memberCount}</strong>{" "}
                {memberCount === 1 ? "assinatura ativa" : "assinaturas ativas"}{" "}
                neste plano. Escolha pra qual plano migrar.
              </div>
              <div>
                <div class="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Migrar membros para
                </div>
                {candidates.length === 0 ? (
                  <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                    Nenhum outro plano ativo disponível. Crie ou ative um plano
                    antes de prosseguir.
                  </div>
                ) : (
                  <div class="space-y-1.5">
                    {candidates.map((p) => (
                      <label
                        key={p.id}
                        class={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm transition ${
                          targetSlug === p.slug
                            ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                            : "border-border bg-background hover:border-primary/40"
                        }`}
                      >
                        <input
                          type="radio"
                          name="target-plan"
                          checked={targetSlug === p.slug}
                          onChange={() => setTargetSlug(p.slug)}
                          class="mt-1"
                        />
                        <div class="min-w-0 flex-1">
                          <div class="flex items-baseline gap-2">
                            <span class="font-medium">{p.name}</span>
                            <code class="rounded bg-muted px-1 text-[10px] text-muted-foreground">
                              {p.slug}
                            </code>
                            {p.isDefault && (
                              <span class="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-700 dark:text-emerald-300">
                                default
                              </span>
                            )}
                          </div>
                          <div class="text-[11px] text-muted-foreground">
                            {brl(p.monthlyCents)}/mês ·{" "}
                            {p.memberCount ?? 0} membro
                            {(p.memberCount ?? 0) === 1 ? "" : "s"} hoje
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {mode === "hard-delete" && (
            <div class="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-xs text-destructive">
              <AlertCircle className="mr-1 inline size-3.5" />
              <strong>O plano sai do admin pra sempre.</strong> A entrada
              fica guardada no banco pra auditoria (cobranças passadas e
              relatórios mantêm o nome do plano), mas não aparece mais em
              nenhuma listagem ou seletor.
            </div>
          )}
        </div>
        <footer class="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={run}
            disabled={busy || (memberCount > 0 && candidates.length === 0)}
            class={`inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${
              mode === "hard-delete"
                ? "bg-destructive hover:brightness-110"
                : "bg-amber-600 hover:brightness-110"
            }`}
          >
            {busy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <ArrowRight className="size-4" />
            )}
            {mode === "hard-delete"
              ? "Migrar e excluir"
              : memberCount > 0
                ? "Migrar e arquivar"
                : "Arquivar"}
          </button>
        </footer>
      </div>
    </div>
  );
}

interface KnownFeature {
  key: string;
  label: string;
  type: "boolean" | "number-or-null";
  description: string;
}
const KNOWN_FEATURES: KnownFeature[] = [
  {
    key: "ai",
    label: "Assistente IA",
    type: "boolean",
    description: "Libera Modo Estudo IA + assistente em provas."
  },
  {
    key: "exportPdf",
    label: "Exportar PDF",
    type: "boolean",
    description: "Permite gerar PDF do banco/prova."
  },
  {
    key: "maxBanks",
    label: "Máx bancos",
    type: "number-or-null",
    description: "Quantidade máxima de bancos acessíveis. Null = ilimitado."
  },
  {
    key: "maxProvas",
    label: "Máx provas",
    type: "number-or-null",
    description: "Quantidade máxima de provas simultâneas. Null = ilimitado."
  },
  {
    key: "lectureMinutesPerMonth",
    label: "Minutos de transcrição/mês",
    type: "number-or-null",
    description:
      "Cota mensal de transcrição+resumo do Gravador de Aulas (Gemini com chave da plataforma). Null = ilimitado. Após esgotar, aluno usa BYOK ou aguarda renovação."
  }
];

function FeaturesEditor({
  features,
  onChange,
  disabled
}: {
  features: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
}) {
  function set(key: string, value: unknown) {
    onChange({ ...features, [key]: value });
  }
  return (
    <div class="rounded-md border border-border bg-background/40 p-3">
      <div class="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Recursos liberados
      </div>
      <div class="space-y-2">
        {KNOWN_FEATURES.map((f) => (
          <FeatureRow
            key={f.key}
            feature={f}
            value={features[f.key]}
            onChange={(v) => set(f.key, v)}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}

function FeatureRow({
  feature,
  value,
  onChange,
  disabled
}: {
  feature: KnownFeature;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
}) {
  if (feature.type === "boolean") {
    return (
      <label class="flex items-start gap-3">
        <Switch
          checked={!!value}
          onCheckedChange={(v) => onChange(v)}
          disabled={disabled}
        />
        <div class="min-w-0 flex-1">
          <div class="text-sm font-medium">{feature.label}</div>
          <p class="text-[10px] text-muted-foreground">{feature.description}</p>
        </div>
      </label>
    );
  }
  // number-or-null
  const unlimited = value === null || value === undefined;
  const numValue = typeof value === "number" ? value : "";
  return (
    <div class="flex items-start gap-3">
      <Switch
        checked={!unlimited}
        onCheckedChange={(on) => {
          if (on) onChange(typeof value === "number" ? value : 0);
          else onChange(null);
        }}
        disabled={disabled}
      />
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium">{feature.label}</span>
          {!unlimited && (
            <input
              type="number"
              min={0}
              value={String(numValue)}
              onInput={(e) =>
                onChange(parseInt((e.target as HTMLInputElement).value, 10) || 0)
              }
              placeholder="Limite"
              title={`Limite de ${feature.label}`}
              class="w-24 rounded-md border border-border bg-background px-2 py-1 text-xs tabular-nums"
              disabled={disabled}
            />
          )}
          {unlimited && (
            <span class="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
              ilimitado
            </span>
          )}
        </div>
        <p class="text-[10px] text-muted-foreground">{feature.description}</p>
      </div>
    </div>
  );
}

function renderFeatureChips(features: Record<string, unknown>) {
  const chips: { label: string; on: boolean }[] = KNOWN_FEATURES.map((f) => {
    const v = features[f.key];
    if (f.type === "boolean") {
      return { label: f.label, on: !!v };
    }
    if (v === null || v === undefined) {
      return { label: `${f.label}: ilimitado`, on: true };
    }
    return { label: `${f.label}: ${v}`, on: true };
  });
  return chips.map((c) => (
    <span
      key={c.label}
      class={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
        c.on
          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
          : "bg-muted text-muted-foreground"
      }`}
    >
      {c.on && <Check className="size-3" />}
      {c.label}
    </span>
  ));
}

function CreatePlanButton() {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  if (!creating) {
    return (
      <button
        type="button"
        onClick={() => setCreating(true)}
        class="inline-flex items-center gap-2 rounded-md border border-dashed border-border bg-background px-4 py-2 text-sm font-medium text-muted-foreground hover:border-primary hover:text-primary"
      >
        <Plus className="size-4" />
        Adicionar plano
      </button>
    );
  }

  async function submit() {
    if (!name.trim()) return;
    await api.createPlatformPlan({ name: name.trim() });
    setName("");
    setCreating(false);
    await load();
  }

  return (
    <div class="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/5 p-3">
      <input
        type="text"
        value={name}
        onInput={(e) => setName((e.target as HTMLInputElement).value)}
        placeholder="Nome do novo plano (ex: Business)"
        class="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
      <button
        type="button"
        onClick={submit}
        disabled={!name.trim()}
        class="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
      >
        Criar
      </button>
      <button
        type="button"
        onClick={() => setCreating(false)}
        class="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-accent"
      >
        Cancelar
      </button>
    </div>
  );
}
